import { useEffect } from "react";
import { useLocation } from "wouter";

/** Message the service worker sends to route an open tab from a notification click. */
type ServiceWorkerNavigateMessage = { type: "okthx:navigate"; path: string };

function isNavigateMessage(data: unknown): data is ServiceWorkerNavigateMessage {
  const message = data as ServiceWorkerNavigateMessage | null;
  return message?.type === "okthx:navigate" && typeof message.path === "string";
}

/**
 * Routes the app in place when a notification is clicked while this tab is
 * already open, instead of letting the service worker reload the document.
 *
 * A warm tab already holds the session, the socket, and the caches the target
 * needs — reloading it to show a thread or an event popover throws all of that
 * away and makes the click feel slow. It also removes the self-navigation case
 * the calendar deep link creates, where the target differs from the current URL
 * only by `?eventId=` (BIG-6898).
 *
 * The acknowledgement goes out on receipt, before routing: it tells the worker a
 * listener exists, and the worker falls back to a document navigation without
 * one (an older build, or a tab still booting).
 *
 * Must be mounted inside the router — it navigates through wouter.
 */
export const useServiceWorkerNavigation = (): void => {
  const [, setLocation] = useLocation();

  useEffect(() => {
    // Captured, not re-read on cleanup: the container is absent in unsupported
    // browsers and in tests, and an unsubscribe must not depend on it still
    // being there.
    const container = navigator.serviceWorker;
    if (!container) return;
    const onMessage = (event: MessageEvent) => {
      if (!isNavigateMessage(event.data)) return;
      event.ports[0]?.postMessage(true);
      setLocation(event.data.path);
    };
    container.addEventListener("message", onMessage);
    return () => container.removeEventListener("message", onMessage);
  }, [setLocation]);
};
